import { useRef, useEffect } from 'react';
import { UserIcon, UsersIcon } from '@heroicons/react/24/outline';

/**
 * OwnerSelector Component
 * 
 * Displays a dropdown menu to select transaction owner (personal, partner, or joint)
 */
const OwnerSelector = ({ 
  transactionId, 
  currentOwnerType = 'personal',
  user,
  onSelectOwner, 
  onCancel 
}) => {
  const ref = useRef(null);
  
  // Handle outside clicks to close the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onCancel();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCancel]);
  
  const handleSelect = (ownerType, ownerDetails = null) => {
    onSelectOwner(transactionId, ownerType, ownerDetails);
  };

  // Get partner name
  const partnerName = user?.partner?.firstName || user?.createdPartner?.firstName || 'Partner';
  const userName = user?.firstName || 'You';

  return (
    <div 
      className="z-40 bg-white shadow-lg rounded-lg overflow-hidden w-48 border border-gray-100 animate-fadeIn"
      ref={ref}
      style={{ 
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)'
      }}
    >
      <style jsx="true">{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out forwards;
        }
      `}</style>
      <div className="overflow-y-auto py-2" role="menu" aria-orientation="vertical">
        <button
          className={`flex items-center w-full px-3 py-2 text-left text-sm transition-colors duration-150 
                    ${currentOwnerType === 'personal' 
                      ? 'bg-gray-100 text-gray-700 font-medium' 
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 bg-white'}`}
          role="menuitem"
          onClick={() => handleSelect('personal')}
        >
          <div className="rounded-full bg-gray-100 p-1 mr-2 flex-shrink-0">
            <UserIcon className="h-3 w-3 text-gray-600" />
          </div>
          <span>{userName}</span>
        </button>
        
        {(user?.partnerId || user?.partner || user?.createdPartner) && (
          <button
            className={`flex items-center w-full px-3 py-2 text-left text-sm transition-colors duration-150 
                      ${currentOwnerType === 'partner' 
                        ? 'bg-blue-100 text-blue-700 font-medium' 
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600 bg-white'}`}
            role="menuitem"
            onClick={() => {
              const ownerDetails = {
                name: partnerName
              };
              handleSelect('partner', ownerDetails);
            }}
          >
            <div className="rounded-full bg-blue-100 p-1 mr-2 flex-shrink-0">
              <UserIcon className="h-3 w-3 text-blue-600" />
            </div>
            <span>{partnerName}</span>
          </button>
        )}
        
        <button
          className={`flex items-center w-full px-3 py-2 text-left text-sm transition-colors duration-150 
                    ${currentOwnerType === 'joint' 
                      ? 'bg-purple-100 text-purple-700 font-medium' 
                      : 'text-gray-700 hover:bg-purple-50 hover:text-purple-600 bg-white'}`}
          role="menuitem"
          onClick={() => handleSelect('joint')}
        >
          <div className="rounded-full bg-purple-100 p-1 mr-2 flex-shrink-0">
            <UsersIcon className="h-3 w-3 text-purple-600" />
          </div>
          <span>Joint</span>
        </button>
      </div>
    </div>
  );
};

export default OwnerSelector;

